import type {
  B2BProduct,
  Category,
  Collection,
  GameTier,
  HomePresentation,
  UserRoom,
  UserRoomProductType,
  View,
  VirtualProduct,
  VirtualProductInteraction,
  VisualCategory,
} from '@features/collection/api/showcase.service.types';
import axios from 'axios';
import { setupUserBlockInterceptor } from '@shared/utils/setupUserBlockInterceptor';

const showcaseApi = axios.create({
  baseURL: import.meta.env.VITE_API_SHOWCASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

setupUserBlockInterceptor(showcaseApi);

export const showcaseService = {
  async getHomePresentation(operationId: string, signal?: AbortSignal) {
    return await showcaseApi.get<HomePresentation>('/public/home', {
      headers: { 'x-operation-id': operationId },
      signal,
    });
  },
  async getViews(signal?: AbortSignal) {
    return await showcaseApi.get<View[]>(`/public/view`, { signal });
  },
  async getViewById(id: string) {
    return await showcaseApi.get<View>(`/public/view/${id}`);
  },
  async getViewBySlug(slug: string, signal?: AbortSignal) {
    return await showcaseApi.get<View>(`/public/view/findBySlug/${slug}`, {
      signal,
    });
  },
  async getCollectionBySlug(slug: string, signal?: AbortSignal) {
    return await showcaseApi.get<Collection>(
      `/public/collection/findBySlug/${slug}`,
      { signal },
    );
  },
  async getCollections(signal?: AbortSignal) {
    return await showcaseApi.get<Collection[]>(`/public/collection`, {
      signal,
    });
  },
  async getCategoryById(id: string) {
    return await showcaseApi.get<Category>(`/public/category/${id}`);
  },
  async getCategoryBySlug(slug: string, signal?: AbortSignal) {
    return await showcaseApi.get<Category>(
      `/public/category/findBySlug/${slug}`,
      { signal },
    );
  },
  async getCategories() {
    return await showcaseApi.get<Category[]>(`/public/category`);
  },
  async getVisualCategoryBySlug(slug: string, signal?: AbortSignal) {
    return await showcaseApi.get<VisualCategory>(
      `/public/visual-category/findBySlug/${slug}`,
      { signal },
    );
  },
  async getProductsByVisualCategories(categories_ids: string[]) {
    return await showcaseApi.post<VisualCategory[]>(
      '/public/products/findByVisualCategories',
      { categories_ids },
    );
  },
  async getProductsByCategory(category_id: string, signal?: AbortSignal) {
    return await showcaseApi.post<VirtualProduct[]>(
      '/public/products/findByCategory',
      {
        category_id,
      },
      { signal },
    );
  },
  async getProductsByCollection(colletionId: string, signal?: AbortSignal) {
    return await showcaseApi.get<VirtualProduct[]>(
      `/public/products/findByCollection/${colletionId}`,
      { signal },
    );
  },
  async getGamesListing(tier?: GameTier, signal?: AbortSignal) {
    return await showcaseApi.get<VirtualProduct[]>(
      `/public/products/gamesListing`,
      {
        params: { tier },
        signal,
      },
    );
  },
  async getB2BCatalog(subscriptionTier?: GameTier) {
    return await showcaseApi.get<B2BProduct[]>(`/public/products/b2bCatalog`, {
      params: { subscriptionTier },
    });
  },
  async getPublicProductById(id: string) {
    return await showcaseApi.get<VirtualProduct>(`/public/products/${id}`);
  },
  async getProductById(id: string) {
    return await showcaseApi.get<VirtualProduct>(`/products/${id}`);
  },
  async getProductsById(ids: string[], signal?: AbortSignal) {
    return await showcaseApi.post<VirtualProduct[]>(
      '/products/findByProductIds',
      {
        product_ids: ids,
      },
      { signal },
    );
  },
  async getProductBySlug(slug: string, signal?: AbortSignal) {
    return await showcaseApi.get<VirtualProduct>(
      `/products/findBySlug/${slug}`,
      { signal },
    );
  },
  async getProductByRoomName(name: string) {
    return await showcaseApi.get<VirtualProduct>(
      `/products/findByRoomName/${name}`,
    );
  },
  // Desenchufado, habilitar en el backend
  async getProductsSearchByPrefix(prefix: string) {
    return await showcaseApi.get<{ word: string; value: VirtualProduct }[]>(
      `/public/products/prefixSearch?prefix=${prefix}`,
    );
  },
  async addVisit(data: { productVirtualId: string }) {
    return await showcaseApi.post<VirtualProductInteraction>(
      `/products/interactions/addVisit`,
      data,
    );
  },
  async updateProductInteractions(
    data: {
      productVirtualId: string;
      favorite?: boolean;
      like?: boolean;
    },
    signal?: AbortSignal,
  ) {
    return await showcaseApi.post<VirtualProductInteraction>(
      `/products/interactions`,
      data,
      { signal },
    );
  },
  async getMyFavorites(signal?: AbortSignal) {
    return await showcaseApi.get<VirtualProduct[]>(`/products/findFavorites`, {
      signal,
    });
  },
  async getMyRooms() {
    return await showcaseApi.get<UserRoom[]>(`/products/rooms/user`);
  },
  async getUserRooms(userId: string) {
    return await showcaseApi.get<UserRoom[]>(`/products/rooms/user/${userId}`);
  },
  async createMyRoom(type: UserRoomProductType, templateId?: string) {
    return await showcaseApi.post<VirtualProduct>(`/products/rooms/user`, {
      type,
      templateId,
    });
  },
};
